import {
  BasketPatchItem,
  ExtendedDtoBasket,
} from '@epicuro-next/data/baskets-dto';
import { BasketNotification } from '@epicuro-next/models/basket';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
  BasketItem,
  BasketRowPrice,
  BasketRowProduct,
  BasketRowsGroup,
  CheckOrderResult,
  ValidationResult,
} from 'graphql-api/baskets-api-interfaces';

import {
  checkBasket,
  checkBasketSuccess,
  clearBasketNotification,
  clearBasketValidations,
  loadBasketSuccess,
  loadDtoBasketSuccess,
  rescheduleBasketItem,
  resetBasket,
  resetBasketCheckedStatus,
  runBasketValidationsSuccess,
  setActiveBasketId,
  setBasketNotification,
  setBasketOperableStatus,
  submitBasketSuccess,
  updateBasketEtag,
  updateBasketItems,
  updateBasketItemsFailure,
  updateBasketItemsSuccess,
  updateBasketShopsFail,
  updateBasketShopsSuccess,
  validateBasket,
  writeItemsInsertOrUpdate,
} from '../actions';

/**
 * This is the key used to index the big-big state
 */
export const basketDataFeatureKey = 'data';

export type ExtendedBasketItem = BasketItem & {
  productInfo: BasketRowProduct;
  priceInfo: BasketRowPrice;
  prepackTitle?: string;
};

function selectId(basket: ExtendedDtoBasket) {
  return basket.basketId;
}

/**
 * Basket state slice interface
 */
export interface State extends EntityState<ExtendedDtoBasket> {
  /** The id of the currently selected basket */
  activeBasketId: string | null;
  /** Validation Results */
  validations: CheckOrderResult | null;
  functionValidations: ValidationResult[] | null;
  isOperable: boolean;
  isValidationSubmitted: boolean;
  reschedulingBasketItem: boolean;
  itemsInsertOrUpdate?: BasketPatchItem[];
  basketNotification?: BasketNotification;
}

export const adapter = createEntityAdapter({
  selectId,
});

/**
 * The initial state of the Basket slice
 */
export const initialState: State = adapter.getInitialState({
  activeBasketId: null,
  validations: null,
  functionValidations: null,
  isValidationSubmitted: false,
  reschedulingBasketItem: false,
  isOperable: true,
  groupedBaskets: null,
});

/**
 * Basket reducer. Reduces shit
 */
export const reducer = createReducer(
  initialState,
  on(loadBasketSuccess, (state): State => {
    return {
      ...state,
      reschedulingBasketItem: false,
    };
  }),
  on(setActiveBasketId, (state, action): State => {
    return {
      ...state,
      activeBasketId: action.basketId,
    };
  }),
  on(
    validateBasket,
    (state): State => ({
      ...state,
      isValidationSubmitted: true,
    }),
  ),
  on(
    checkBasket,
    (state): State => ({
      ...state,
      validations: null,
    }),
  ),
  on(
    checkBasketSuccess,
    (state, { validationResults }): State => ({
      ...state,
      validations: validationResults,
    }),
  ),
  on(runBasketValidationsSuccess, (state, { validationResults }) => ({
    ...state,
    functionValidations: validationResults,
  })),
  on(
    clearBasketValidations,
    (state): State => ({
      ...state,
      validations: null,
      functionValidations: null,
      isValidationSubmitted: false,
    }),
  ),
  on(
    setBasketOperableStatus,
    (state, { isOperable }): State => ({
      ...state,
      isOperable,
    }),
  ),
  on(
    resetBasket,
    (state): State => ({
      ...state,
      activeBasketId: null,
      validations: null,
    }),
  ),
  on(
    resetBasketCheckedStatus,
    (state, { basketId }): State =>
      adapter.updateOne(
        { id: basketId, changes: { checkOrderExpiryUtc: undefined } },
        state,
      ),
  ),
  on(
    setBasketNotification,
    (state, notification): State =>
      state.reschedulingBasketItem
        ? state
        : {
            ...state,
            basketNotification: {
              ...state.basketNotification,
              ...notification,
            },
          },
  ),
  on(
    writeItemsInsertOrUpdate,
    (state, { items }): State => ({
      ...state,
      itemsInsertOrUpdate: items,
    }),
  ),
  on(
    clearBasketNotification,
    (state): State => ({
      ...state,
      itemsInsertOrUpdate: undefined,
      basketNotification: undefined,
    }),
  ),
  on(
    updateBasketItems,
    (state): State => ({
      ...state,
      itemsInsertOrUpdate: undefined,
      basketNotification: undefined,
    }),
  ),
  on(
    rescheduleBasketItem,
    (state): State => ({
      ...state,
      reschedulingBasketItem: true,
    }),
  ),
  on(
    updateBasketShopsSuccess,
    updateBasketShopsFail,
    updateBasketItemsSuccess,
    updateBasketItemsFailure,
    (state): State => ({
      ...state,
      reschedulingBasketItem: false,
    }),
  ),
  on(
    loadDtoBasketSuccess,
    (state, { basket }): State => adapter.setOne(basket, state),
  ),
  on(submitBasketSuccess, (state, { basketId }): State => {
    if (state.entities[basketId]) {
      return adapter.setOne(
        {
          ...state.entities[basketId],
          status: 'Submitted',
        } as ExtendedDtoBasket,
        state,
      );
    }

    return state;
  }),
  on(updateBasketEtag, (state, { basketId, etag }) => {
    return adapter.updateOne(
      {
        id: basketId,
        changes: {
          etag,
        },
      },
      state,
    );
  }),
);

export function basketItemReducer(
  groups?: BasketRowsGroup[] | null,
): ExtendedBasketItem[] {
  if (isNullish(groups) || groups.length === 0) {
    return [];
  }

  return groups.reduce((acc, group) => {
    if (isNonNullish(group.rows)) {
      return acc.concat(
        group.rows.flatMap((row) => {
          return row.basketItems.map((basketItem) => {
            return {
              ...basketItem,
              productInfo: row.product,
              priceInfo: row.priceInfo,
            };
          });
        }),
      );
    }

    return acc.concat(basketItemReducer(group.groups));
  }, [] as ExtendedBasketItem[]);
}
